import { Component } from 'react';

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // In production, send to error tracking (Sentry, etc.)
    console.error('[ErrorBoundary]', error, errorInfo);
    if (import.meta.env.PROD) {
      // reportErrorToSentry(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return this.props.fallback ? (
        this.props.fallback(this.state.error, this.handleReset)
      ) : (
        <AppCrashScreen error={this.state.error} onReset={this.handleReset} />
      );
    }
    return this.props.children;
  }
}

const AppCrashScreen = ({ error, onReset }) => (
  <div style={{
    minHeight: '100dvh', background: '#0A0A0F', color: '#fff',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '32px', textAlign: 'center',
    fontFamily: "'DM Sans', system-ui, sans-serif",
  }}>
    <div style={{ fontSize: 56, marginBottom: 16 }}>💥</div>
    <h2 style={{ fontSize: 24, fontWeight: 900, marginBottom: 8 }}>Something snapped</h2>
    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, marginBottom: 8, maxWidth: 280 }}>
      SplitSnap hit an unexpected error. Your session data is safe.
    </p>
    {import.meta.env.DEV && error && (
      <pre style={{
        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 12, padding: '12px 16px', fontSize: 11,
        color: '#FF6B6B', maxWidth: '100%', overflow: 'auto',
        textAlign: 'left', marginBottom: 16, maxHeight: 120,
      }}>
        {error?.message}
      </pre>
    )}
    <button
      onClick={onReset}
      style={{
        padding: '16px 32px', borderRadius: 16, border: 'none',
        background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
        color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      Back to Home
    </button>
  </div>
);

/**
 * Lightweight screen-level boundary for graceful degradation
 * without taking down the whole app
 */
export const ScreenErrorBoundary = ({ children, screenName }) => (
  <ErrorBoundary
    fallback={(error, reset) => (
      <div style={{
        minHeight: '100dvh', background: '#0A0A0F', color: '#fff',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: 24, fontFamily: "'DM Sans', sans-serif",
      }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <p style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
          {screenName} failed to load
        </p>
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, marginBottom: 20 }}>
          {import.meta.env.DEV ? error?.message : 'Please try again'}
        </p>
        <button onClick={reset} style={{
          padding: '12px 24px', borderRadius: 14, border: 'none',
          background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
          color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
        }}>
          Retry
        </button>
      </div>
    )}
  >
    {children}
  </ErrorBoundary>
);
